import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core'
import { AmplitudeUnmaskDirective } from '../../directives'
import { Icon, IconComponent } from '../icon'
import { TooltipAlignment, TooltipModule, TooltipPosition } from '../tooltip'

export enum BadgeColors {
  MediumGrey = 'medium-grey',
  LightGrey = 'light-grey',
  Primary = 'primary',
  DarkPrimary = 'dark-primary',
  Blueberry = 'blueberry',
  Apple = 'apple',
  Error = 'error',
  Mint = 'mint',
  Lemon = 'lemon',
  Mandarin = 'mandarin',
  Watermelon = 'watermelon',
  Brand = 'brand',
  Grape = 'grape',
  Secondary = 'secondary',
}
export type BadgeColor = `${BadgeColors}`

export const BadgeDisplayTypes = {
  Large: 'large',
  Default: 'default',
  Small: 'small',
  Tile: 'tile',
  Inline: 'inline',
} as const
export type BadgeDisplayType = (typeof BadgeDisplayTypes)[keyof typeof BadgeDisplayTypes]

export interface BadgeConfig {
  color: BadgeColor
  icon: Icon
  text: string
  ariaLabel?: string
  tooltipLabel?: string
}

/**
 * Component for displaying a concise status indicator or label. Badge content can be just text,
 * just an icon, or both.
 *
 * @example
 * ``` html
 * <ftr-badge text="New" icon="RecordLarge" color="primary" />
 * ```
 */
@Component({
  selector: 'ftr-badge',
  hostDirectives: [AmplitudeUnmaskDirective],
  templateUrl: './badge.component.html',
  styleUrl: './badge.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [IconComponent, TooltipModule],
})
export class BadgeComponent {
  /** @see {@link BadgeColors} */
  readonly color = input<BadgeColor | BadgeColors>(BadgeColors.MediumGrey)
  /**
   * The text to display in the badge.
   */
  readonly text = input<string>()
  /**
   * The icon to display in the badge.
   */
  readonly icon = input<Icon>()

  /** @see {@link BadgeDisplayTypes} */
  readonly displayType = input<BadgeDisplayType>(BadgeDisplayTypes.Default)

  /** Explicitly set width when text should truncate, otherwise badge width spans content. */
  readonly maxWidth = input<string>()

  /**
   * The tooltip label for the badge.
   */
  readonly tooltipLabel = input<string>()

  /** @see {@link TooltipPosition} */
  readonly tooltipPosition = input<TooltipPosition>('below')
  readonly tooltipAlignment = input<TooltipAlignment>('auto')

  /**
   * The aria label for the badge.  If not provided, the 'tooltipLabel' will be used.
   */
  readonly ariaLabel = input<string>()
  /**
   * The aria role for the badge.
   * Defaults to 'status'.
   */
  readonly ariaRole = input('status')

  protected readonly getTooltipLabel = computed<string>(() => {
    return this.tooltipLabel() ?? (this.maxWidth() && this.text()) ?? ''
  })

  protected readonly getAriaLabel = computed<string | undefined>(() => {
    return this.ariaLabel() || this.tooltipLabel()
  })

  protected readonly getClasses = computed<Record<string, boolean>>(() => ({
    badge: true,
    [`badge--color-${this.color()}`]: true,
    'badge--inline': this.displayType() === 'inline',
    'badge--small': this.displayType() === 'small',
    'badge--large': this.displayType() === 'large',
    'badge--tile': this.displayType() === 'tile',
    'badge--truncated-width': !!this.maxWidth(),
  }))
}
